import React, { useEffect, useState, useCallback } from 'react';
import {
    Box, Button, TextField, Typography, Paper,
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Grid,
    InputAdornment, Divider, CircularProgress,
    Dialog, DialogTitle, DialogContent,
    DialogActions, Zoom, TablePagination
} from '@mui/material';

import { Search, Delete, Edit, Save, PersonAdd } from '@mui/icons-material';
import Swal from 'sweetalert2';

import { auth, db } from '../../firebase';
import {
    collection, addDoc, Timestamp,
    query, orderBy, getDocs,
    deleteDoc, doc, updateDoc
} from 'firebase/firestore';

import Menu from '../../panel/Menu';

export default function Customers() {

    const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
    });

    const [customers, setCustomers] = useState([]);
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [loading, setLoading] = useState(true);

    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [form, setForm] = useState({
        custname: "",
        mobile: "",
        email: "",
        address: "",
        city: "",
        state: "",
        gender: "",
        gstname: "",
        gstno: "",
        documenttype: "",
        idno: ""
    });

    // ================= FETCH DATA =================

    const fetchCustomers = useCallback(async () => {

        setLoading(true);

        try {

            const q = query(
                collection(db, "customers")
            );

            const snapshot = await getDocs(q);

            const data = snapshot.docs.map(doc => {

                const raw = doc.data().customerData || {};

                return {
                    id: doc.id,
                    custname: raw.custname || "",
                    mobile: raw.mobile || "",
                    email: raw.email || "",
                    address: raw.address || "",
                    city: raw.city || "",
                    state: raw.state || "",
                    gender: raw.gender || "",
                    gstname: raw.gstname || "",
                    gstno: raw.gstno || "",
                    documenttype: raw.documenttype || "",
                    idno: raw.idno || "",
                    createdAt: doc.data().createdAt
                };

            });

            setCustomers(data);
            setFilteredCustomers(data);

        } catch (error) {

            console.error(error);
            Toast.fire({ icon: "error", title: "Error loading data" });

        }

        setLoading(false);

    }, []);

    useEffect(() => {
        fetchCustomers();
    }, [fetchCustomers]);

    // ================= SEARCH =================
    useEffect(() => {

        const delay = setTimeout(() => {

            const search = searchText.toLowerCase();

            const filtered = customers.filter(c => {
                const name = c.custname || "";
                const mobile = c.mobile || "";
                const gst = c.gstno || "";

                return (
                    name.toLowerCase().includes(search) ||
                    mobile.includes(search) ||
                    gst.toLowerCase().includes(search)
                );
            });

            setFilteredCustomers(filtered);
            setPage(0);

        }, 300);

        return () => clearTimeout(delay);

    }, [searchText, customers]);

    // ================= PAGINATION =================

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {

        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);

    };

    // ================= FORM =================

    const handleOpen = (customer = null) => {

        if (customer) {

            setForm(customer);
            setEditId(customer.id);

        } else {

            resetForm();

        }

        setOpen(true);

    };

    const handleClose = () => {

        setOpen(false);
        resetForm();

    };

    const handleChange = (e) => {

        setForm({
            ...form,
            [e.target.name]: e.target.value
        });

    };

    // ================= SAVE / UPDATE =================

    const handleSubmit = async () => {

        if (!form.custname || !form.mobile) {

            Toast.fire({
                icon: "error",
                title: "Name & Mobile Required"
            });

            return;

        }

        try {

            const payload = {
                customerData: form,
                updatedAt: Timestamp.now()
            };

            if (editId) {

                await updateDoc(doc(db, "customers", editId), payload);

                Toast.fire({
                    icon: "success",
                    title: "Customer Updated"
                });

            } else {

                await addDoc(collection(db, "customers"), {
                    ...payload,
                    createdAt: Timestamp.now(),
                    uid: auth.currentUser?.uid || "admin"
                });

                Toast.fire({
                    icon: "success",
                    title: "Customer Added"
                });

            }

            fetchCustomers();
            handleClose();

        } catch {

            Toast.fire({
                icon: "error",
                title: "Operation Failed"
            });

        }

    };

    // ================= DELETE =================

    const handleDelete = async (id) => {

        const result = await Swal.fire({
            title: "Delete record?",
            icon: "warning",
            showCancelButton: true
        });

        if (!result.isConfirmed) return;

        await deleteDoc(doc(db, "customers", id));

        Toast.fire({
            icon: "success",
            title: "Deleted"
        });

        fetchCustomers();

    };

    const resetForm = () => {

        setForm({
            custname: "",
            mobile: "",
            email: "",
            address: "",
            city: "",
            state: "",
            gender: "",
            gstname: "",
            gstno: "",
            documenttype: "",
            idno: ""
        });

        setEditId(null);

    };

    return (

        <Box>

            <Menu />

           <Box className="content-wrapper p-2" sx={{ position: 'relative', zIndex: 1 }}>

                {/* HEADER */}

                <Paper sx={{ p: 2, mb: 2, display: 'flex', justifyContent: 'space-between' }}>

                    <Typography variant="h5" fontWeight="bold">
                        Customer Directory
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2 }}>

                      

                        <Button
                            variant="contained"
                            startIcon={<PersonAdd />}
                            onClick={() => handleOpen()}
                        >
                            Add
                        </Button>

                    </Box>

                </Paper>

                {/* TABLE */}

                <Paper className='p-1'>
                      <TextField
                            size="small"
                            placeholder="Search name / mobile / gst"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                )
                            }}
                        />

                    <TableContainer>

                        <Table stickyHeader size="small">

                            <TableHead>

                                <TableRow>

                                    <TableCell sx={{ fontWeight: 'bold' }}>Sr</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Mobile</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>City</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>State</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Doc</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Action</TableCell>

                                </TableRow>

                            </TableHead>

                            <TableBody>

                                {filteredCustomers
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((c, index) => {

                                        const serial = page * rowsPerPage + index + 1;

                                        return (

                                            <TableRow key={c.id} hover>

                                                <TableCell>{serial}</TableCell>
                                                <TableCell>{c.custname}</TableCell>
                                                <TableCell>{c.mobile}</TableCell>
                                                <TableCell>{c.email}</TableCell>
                                                <TableCell>{c.city}</TableCell>
                                                <TableCell>{c.state}</TableCell>
                                                <TableCell>{c.documenttype}</TableCell>
                                                <TableCell>{c.idno}</TableCell>

                                                <TableCell align="center">

                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleOpen(c)}
                                                    >
                                                        <Edit />
                                                    </IconButton>

                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDelete(c.id)}
                                                    >
                                                        <Delete />
                                                    </IconButton>

                                                </TableCell>

                                            </TableRow>

                                        )

                                    })}

                            </TableBody>

                        </Table>

                        {loading &&
                            <Box sx={{ textAlign: 'center', p: 4 }}>
                                <CircularProgress />
                            </Box>}

                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={filteredCustomers.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />

                </Paper>

                {/* FORM */}

                <Dialog
                    open={open}
                    onClose={handleClose}
                    fullWidth
                    maxWidth="md"
                    TransitionComponent={Zoom}
                >

                    <DialogTitle>

                        {editId ? "Edit Customer" : "Add Customer"}

                    </DialogTitle>

                    <Divider />

                    <DialogContent>

                        <Grid container spacing={2} sx={{ mt: 1 }}>

                            {Object.keys(form).map((key) => (
                                <Grid item xs={12} md={4} key={key}>
                                    <TextField
                                        fullWidth
                                        label={key}
                                        name={key}
                                        value={form[key]}
                                        onChange={handleChange}
                                    />
                                </Grid>
                            ))}

                        </Grid>

                    </DialogContent>

                    <DialogActions>

                        <Button onClick={handleClose}>
                            Cancel
                        </Button>

                        <Button
                            variant="contained"
                            startIcon={<Save />}
                            onClick={handleSubmit}
                        >
                            Save
                        </Button>

                    </DialogActions>

                </Dialog>

            </Box>

        </Box>

    );

}